"use client";

import { useState, useRef } from "react";
import Link from "next/link";

interface IntroVideoSectionProps {
  isFa: boolean;
}

export default function IntroVideoSection({ isFa }: IntroVideoSectionProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVertical, setIsVertical] = useState(true); // پیش‌فرض عمودی (با توجه به ویدیو 464x848)
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const vid = e.currentTarget;
    if (vid.videoWidth && vid.videoHeight) {
      setIsVertical(vid.videoWidth < vid.videoHeight);
    }
  };

  return (
    <section className="section-wrapper" style={{ marginTop: "60px", paddingTop: "50px" }}>
      {/* Header */}
      <div className="section-header" style={{ marginBottom: "35px" }}>
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          backgroundColor: "rgba(14, 165, 233, 0.1)",
          border: "1px solid rgba(14, 165, 233, 0.3)",
          color: "#0284c7",
          padding: "5px 16px",
          borderRadius: "30px",
          fontSize: "13px",
          fontWeight: "700",
          marginBottom: "14px"
        }}>
          <span>{isFa ? "ویدیوی معرفی رسمی پلتفرم" : "Official Intro Video"}</span>
        </div>

        <h2 className="section-title">
          {isFa ? "آشنایی با " : "Discover "}
          <span>{isFa ? "پس‌فاند" : "PassFund"}</span>
          {isFa ? " در ۲ دقیقه" : " in 2 Minutes"}
        </h2>
        <p className="section-desc">
          {isFa 
            ? "نحوه کار با پلتفرم، مراحل عبور از چالش و قوانین شفاف تامین سرمایه معاملاتی را در این ویدیوی کوتاه مشاهده کنید."
            : "Watch this short video to learn how the platform works, challenge phases, and transparent funding rules."}
        </p>
      </div>

      {/* Main Container - 2 Columns for Vertical Video, or Wide Centered for Landscape */}
      <div 
        className="intro-video-container"
        style={{
          maxWidth: "1200px",
          flexDirection: !isVertical ? "column" : undefined,
        }}
      >
        
        {/* Right / Left Side Features (for Vertical Video) */}
        {isVertical && (
          <div 
            className="intro-video-features"
            style={{
              textAlign: isFa ? "right" : "left"
            }}
          >
            <div>
              <div style={{ 
                color: "#0ea5e9", 
                fontSize: "13px", 
                fontWeight: "800", 
                letterSpacing: "1px", 
                textTransform: "uppercase", 
                marginBottom: "8px" 
              }}>
                {isFa ? "مسیر تریدر شدن" : "The Trader Path"}
              </div>
              <h3 style={{ 
                fontSize: "24px", 
                fontWeight: "900", 
                color: "#0f172a", 
                margin: "0 0 12px 0",
                lineHeight: "1.4"
              }}>
                {isFa ? "تامین سرمایه تا ۲۵,۰۰۰ دلار با شرایط کاملاً منصفانه" : "Funded Capital Up to $25K"}
              </h3>
              <p style={{ color: "#64748b", fontSize: "14px", lineHeight: "1.8", margin: 0 }}>
                {isFa 
                  ? "در پس‌فاند ما به مهارت معاملاتی شما اعتماد داریم. حساب معاملاتی متاتریدر ۵ را تحویل بگیرید، بدون استرس زمان ترید کنید و سهم سود خود را نقداً به ولت تتر انتقال دهید."
                  : "We believe in your trading talent. Get your MT5 account, trade without time limits, and withdraw your profit split directly to your USDT wallet."}
              </p>
            </div>

            {/* Feature Points */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{
                backgroundColor: "#ffffff",
                padding: "14px 18px",
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
              }}>
                <div>
                  <strong style={{ fontSize: "14px", color: "#1e293b", display: "block", marginBottom: "3px" }}>
                    {isFa ? "چالش‌های ۲ مرحله‌ای شفاف" : "2-Phase Fair Challenges"}
                  </strong>
                  <span style={{ fontSize: "12px", color: "#64748b", lineHeight: "1.5" }}>
                    {isFa ? "تارگت سود ۸٪ و ۵٪ بدون محدودیت زمانی یا قوانین پنهان" : "8% & 5% profit targets with no time limits"}
                  </span>
                </div>
              </div>

              <div style={{
                backgroundColor: "#ffffff",
                padding: "14px 18px",
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
              }}>
                <div>
                  <strong style={{ fontSize: "14px", color: "#1e293b", display: "block", marginBottom: "3px" }}>
                    {isFa ? "برداشت سریع و تضمین‌شده سود" : "Fast & Guaranteed Payouts"}
                  </strong>
                  <span style={{ fontSize: "12px", color: "#64748b", lineHeight: "1.5" }}>
                    {isFa ? "پردازش درخواست‌ها و واریز تتر در کمتر از ۲۴ ساعت کاری" : "Processed and sent to USDT wallet in under 24 hours"}
                  </span>
                </div>
              </div>

              <div style={{
                backgroundColor: "#ffffff",
                padding: "14px 18px",
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
              }}>
                <div>
                  <strong style={{ fontSize: "14px", color: "#1e293b", display: "block", marginBottom: "3px" }}>
                    {isFa ? "سرورهای قدرتمند متاتریدر ۵" : "MetaTrader 5 High-Speed Servers"}
                  </strong>
                  <span style={{ fontSize: "12px", color: "#64748b", lineHeight: "1.5" }}>
                    {isFa ? "پایداری بالا، کمترین اسپرد در بازار و بدون اسلیپیج" : "Lowest spreads, ultra-fast execution, zero lag"}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "6px" }}>
              <Link 
                href={isFa ? "/fa/panel/buy-plan" : "/en/panel/buy-plan"} 
                className="neon-btn"
                style={{ padding: "14px 32px", fontSize: "15px", display: "inline-flex" }}
              >
                {isFa ? "خرید چالش معاملاتی ←" : "Buy Challenge Plan →"}
              </Link>
            </div>
          </div>
        )}

        {/* Video Player Box (Adaptive Smartphone Frame for Vertical Video) */}
        <div 
          className="intro-video-player-box"
          style={{
            width: !isVertical ? "100%" : undefined,
            maxWidth: !isVertical ? "960px" : undefined,
          }}
        >
          {/* Glowing Aura Behind Frame */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            background: "radial-gradient(circle, rgba(14, 165, 233, 0.25) 0%, transparent 70%)",
            filter: "blur(35px)",
            zIndex: 0,
            pointerEvents: "none"
          }}></div>

          {/* Smartphone-Styled Frame */}
          <div style={{
            position: "relative",
            zIndex: 1,
            background: "#090d16",
            border: "3px solid #1e293b",
            borderRadius: isVertical ? "38px" : "22px",
            boxShadow: "0 25px 60px -15px rgba(14, 165, 233, 0.35), 0 0 0 1px rgba(14, 165, 233, 0.3)",
            padding: isVertical ? "12px" : "10px",
            overflow: "hidden"
          }}>
            {/* Top Speaker Notch (for Smartphone Mockup) */}
            {isVertical && (
              <div style={{
                width: "60px",
                height: "5px",
                backgroundColor: "#334155",
                borderRadius: "10px",
                margin: "4px auto 10px auto"
              }}></div>
            )}

            {/* Video Wrapper matching 464:848 (Vertical) or 16:9 (Landscape) */}
            <div style={{
              position: "relative",
              width: "100%",
              aspectRatio: isVertical ? "464 / 848" : "16 / 9",
              borderRadius: isVertical ? "28px" : "16px",
              overflow: "hidden",
              backgroundColor: "#020617"
            }}>
              <video
                ref={videoRef}
                controls={isPlaying}
                playsInline
                preload="metadata"
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block"
                }}
              >
                <source src="/videos/intro.mp4" type="video/mp4" />
                <source src="/videos/intro.mp4.mp4" type="video/mp4" />
                {isFa ? "مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند." : "Your browser does not support HTML5 video."}
              </video>

              {/* Custom Play Overlay (Shown when paused / before click) */}
              {!isPlaying && (
                <div 
                  onClick={handlePlayClick}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(to top, rgba(9, 13, 22, 0.85) 0%, rgba(9, 13, 22, 0.2) 60%, rgba(9, 13, 22, 0.4) 100%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 2,
                    padding: "20px",
                    textAlign: "center"
                  }}
                >
                  {/* Glowing Play Button */}
                  <div style={{
                    width: "76px",
                    height: "76px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 35px rgba(14, 165, 233, 0.8), inset 0 0 15px rgba(255, 255, 255, 0.4)",
                    border: "2px solid rgba(255, 255, 255, 0.6)",
                    transition: "transform 0.2s ease"
                  }}>
                    <svg 
                      width="32" 
                      height="32" 
                      viewBox="0 0 24 24" 
                      fill="#ffffff" 
                      style={{ marginRight: isFa ? "0" : "-4px", marginLeft: isFa ? "-4px" : "0" }}
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>

                  <div style={{
                    marginTop: "16px",
                    color: "#ffffff",
                    fontSize: "15px",
                    fontWeight: "800",
                    textShadow: "0 2px 8px rgba(0,0,0,0.8)"
                  }}>
                    {isFa ? "پخش ویدیوی معرفی" : "Play Intro Video"}
                  </div>

                  <span style={{
                    marginTop: "8px",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    color: "#e0f2fe",
                    padding: "3px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}>
                    {isFa ? "ویدیوی کوتاه ۲ دقیقه‌ای" : "2 Minutes Video"}
                  </span>
                </div>
              )}
            </div>

            {/* Bottom Home Indicator Bar (for Smartphone Mockup) */}
            {isVertical && (
              <div style={{
                width: "90px",
                height: "4px",
                backgroundColor: "#475569",
                borderRadius: "10px",
                margin: "10px auto 4px auto"
              }}></div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}
